import { FileCreateParams, FileObject, FileType } from '@citruscamps/citrus-client'
import {
  faFile,
  faFileAudio,
  faFileCsv,
  faFileExcel,
  faFileImage,
  faFilePdf,
  faFilePowerpoint,
  faFileVideo,
  faFileWord,
  faSpinner,
} from '@fortawesome/pro-duotone-svg-icons'
import { faCalendarDays, faTimes } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome'
import React from 'react'
import { TruncatedText } from '../TruncatedText/TruncatedText'
import './FileList.scss'

type PartialFileObject =
  | (Partial<Pick<FileObject, 'id' | 'url'>> &
      Omit<FileObject, 'id' | 'object' | 'url' | 'created' | 'created_by'>)
  | FileCreateParams

interface IFileListProps {
  files?: PartialFileObject[]
  isCondensed?: boolean
  onRemove?: (file: PartialFileObject, index: number) => void | Promise<void>
}

export const FileList = ({
  files,
  isCondensed = false,
  onRemove: handleRemove,
}: IFileListProps) => (
  <ul className="file-list list-unstyled">
    {files?.map((file: PartialFileObject, index: number) => (
      <li className="list-inline-item m-2" key={'id' in file && file.id ? file.id : file?.filename}>
        <div className="d-flex">
          {isCondensed && (
            <TruncatedText title={file.filename} style={{ maxWidth: 100 }}>
              <FileLink file={file} />
            </TruncatedText>
          )}
          {!isCondensed && <FileLink file={file} />}
          {!!handleRemove && (
            <button
              type="button"
              disabled={!('url' in file) || !file?.url}
              className="btn btn-link"
              onClick={() => handleRemove(file, index)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>
      </li>
    ))}
  </ul>
)

export const FileLink = ({ file }: { file: PartialFileObject }) => (
  <>
    {'url' in file && !!file?.url && (
      <a href={file?.url} target="_blank" rel="noopener noreferrer">
        <FileIcon filetype={file.filetype} url={'url' in file ? file.url : undefined} />
        {file.filename}
      </a>
    )}
    {(!('url' in file) || !file?.url) && <>{file.filename}</>}
  </>
)

export const FileIcon = ({
  filetype,
  url,
  ...props
}: { filetype: FileType; url?: string } & Partial<FontAwesomeIconProps>) => {
  if (!url) return <FontAwesomeIcon icon={faSpinner} className="me-2" pulse {...props} />
  switch (filetype) {
    case 'image/bmp':
    case 'image/gif':
    case 'image/jpeg':
    case 'image/png':
    case 'image/svg+xml':
    case 'image/tiff':
    case 'image/webp':
      return <FontAwesomeIcon icon={faFileImage} className="me-2" {...props} />
    case 'audio/3gpp':
    case 'audio/flac':
    case 'audio/aac':
    case 'audio/midi':
    case 'audio/x-midi':
    case 'audio/mpg':
    case 'audio/mpeg':
    case 'audio/mp3':
    case 'audio/mp4':
    case 'audio/ogg':
    case 'audio/opus':
    case 'audio/wav':
    case 'audio/webm':
      return <FontAwesomeIcon icon={faFileAudio} className="me-2" {...props} />
    case 'video/3gpp':
    case 'video/x-msvideo':
    case 'video/mpeg':
    case 'video/mp4':
    case 'video/ogg':
    case 'video/mp2t':
    case 'video/quicktime':
    case 'video/webm':
      return <FontAwesomeIcon icon={faFileVideo} className="me-2" {...props} />
    case 'application/msword':
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return <FontAwesomeIcon icon={faFileWord} className="me-2" {...props} />
    case 'application/vnd.ms-powerpoint':
    case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
      return <FontAwesomeIcon icon={faFilePowerpoint} className="me-2" {...props} />
    case 'application/vnd.ms-excel':
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      return <FontAwesomeIcon icon={faFileExcel} className="me-2" {...props} />
    case 'application/pdf':
      return <FontAwesomeIcon icon={faFilePdf} className="me-2" {...props} />
    case 'text/csv':
      return <FontAwesomeIcon icon={faFileCsv} className="me-2" {...props} />
    case 'text/calendar':
      return <FontAwesomeIcon icon={faCalendarDays} className="me-2" {...props} />
    default:
      return <FontAwesomeIcon icon={faFile} className="me-2" {...props} />
  }
}
